import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Fab,
  Collapse,
  Alert,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDistanceToNow } from 'date-fns';
import { projectNotesApi, workersApi, authApi } from '../services/api';
import ImageViewer from './ImageViewer';
import { compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';
import { renameFile } from '../utils/fileNaming';

const MESSAGES_PER_PAGE = 20;
const THUMBNAIL_SIZE = 50;

const ProjectNotesTab = ({ projectId }) => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [workers, setWorkers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [mentionMenuAnchor, setMentionMenuAnchor] = useState(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [error, setError] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textFieldRef = useRef(null);
  const isScrollingToBottom = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadCurrentUser();
    loadInitialNotes();
    loadWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    // Filter notes based on search query
    if (searchQuery.trim()) {
      const filtered = notes.filter(note => 
        note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.worker_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredNotes(filtered);
    } else {
      setFilteredNotes(notes);
    }
  }, [notes, searchQuery]);

  useEffect(() => {
    // Auto-scroll to bottom only for new messages (not when loading older ones)
    if (!isScrollingToBottom.current && currentPage === 1) {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredNotes, currentPage]);

  useEffect(() => {
    // Handle scroll event
    const handleScroll = () => {
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
        const isAtTop = scrollTop < 100;
        
        setShowScrollButton(!isAtBottom && filteredNotes.length > 0);
        
        // Load older messages when scrolling to top
        if (isAtTop && hasMoreMessages && !loadingOlder && !searchQuery) {
          loadOlderMessages();
        }
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredNotes.length, hasMoreMessages, loadingOlder, searchQuery]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      isScrollingToBottom.current = true;
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        isScrollingToBottom.current = false;
      }, 1000);
    }
  }, []);

  const loadCurrentUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error loading current user:', error);
      setError('Failed to load user information');
    }
  };

  const loadInitialNotes = async () => {
    try {
      setLoading(true);
      const response = await projectNotesApi.getByProjectId(projectId, MESSAGES_PER_PAGE, 0);
      const newNotes = response.data || [];
      setNotes(newNotes);
      setHasMoreMessages(newNotes.length === MESSAGES_PER_PAGE);
      setCurrentPage(1);
      setError('');
    } catch (error) {
      console.error('Error loading notes:', error);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const loadOlderMessages = async () => {
    if (loadingOlder || !hasMoreMessages) return;
    
    try {
      setLoadingOlder(true);
      const nextPage = currentPage + 1;
      const offset = (nextPage - 1) * MESSAGES_PER_PAGE;
      const response = await projectNotesApi.getByProjectId(projectId, MESSAGES_PER_PAGE, offset);
      const olderNotes = response.data || [];
      
      if (olderNotes.length > 0) {
        // Since notes come in DESC order from API, we prepend them to the beginning
        setNotes(prev => [...olderNotes, ...prev]);
        setCurrentPage(nextPage);
        setHasMoreMessages(olderNotes.length === MESSAGES_PER_PAGE);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setLoadingOlder(false);
    }
  };

  const loadWorkers = async () => {
    try {
      const response = await workersApi.getByProjectId(projectId);
      setWorkers(response.data);
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  };

  const handleSubmitNote = async () => {
    if (!newNote.trim() && selectedPhotos.length === 0) return;

    setLoading(true);
    setError('');
    
    try {
      if (selectedPhotos.length > 0) {
        const formData = new FormData();
        formData.append('content', newNote || 'Photo attachment');
        
        selectedPhotos.forEach((photo, index) => {
          formData.append(`photo_${index}`, photo.file);
        });

        await projectNotesApi.createWithPhotos(projectId, formData);
      } else {
        await projectNotesApi.create(projectId, { content: newNote });
      }
      
      setNewNote('');
      setSelectedPhotos([]);
      await loadInitialNotes(); // Reload recent messages
    } catch (error) {
      console.error('Error creating note:', error);
      setError('Failed to send note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = async (event) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files);
    if (warning) {
      setError(warning);
      return;
    }

    const validFiles = selectedFiles.filter(file => {
      if (file.type.startsWith('image/')) {
        if (file.size <= 10 * 1024 * 1024) {
          return true;
        } else {
          setError('File size must be under 10MB');
          return false;
        }
      } else {
        setError('Only image files are allowed');
        return false;
      }
    });

    // Compress images before creating previews
    const compressedFiles = await compressImages(validFiles);

    // Rename files with UUID to avoid conflicts
    const renamedFiles = compressedFiles.map(file => renameFile(file));

    const newPhotos = renamedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setSelectedPhotos(prev => [...prev, ...newPhotos]);
    setError('');
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleNoteChange = (event) => {
    const value = event.target.value;
    const position = event.target.selectionStart;
    
    setNewNote(value);
    setCursorPosition(position);

    const textBeforeCursor = value.substring(0, position);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
        setMentionSearch(textAfterAt);
        setMentionMenuAnchor(event.target);
      } else {
        setMentionMenuAnchor(null);
      }
    } else {
      setMentionMenuAnchor(null);
    }
  };

  const handleMentionSelect = (worker) => {
    const textBeforeCursor = newNote.substring(0, cursorPosition);
    const textAfterCursor = newNote.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeAt = textBeforeCursor.substring(0, lastAtIndex);
      const workerMention = `@${worker.name.replace(/\s+/g, '')}`;
      const newText = beforeAt + workerMention + ' ' + textAfterCursor;
      
      setNewNote(newText);
      setMentionMenuAnchor(null);
      setMentionSearch('');
      
      setTimeout(() => {
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 100);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitNote();
    }
  };

  const getFilteredWorkers = () => {
    if (!mentionSearch) return workers;
    return workers.filter(worker =>
      worker.name.toLowerCase().includes(mentionSearch.toLowerCase())
    );
  };

  const formatNoteTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  const renderNoteContent = (content) => {
    const mentionRegex = /@(\w+)/g;
    const parts = content.split(mentionRegex);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <Chip
            key={index}
            label={`@${part}`}
            size="small"
            color="secondary" // Always visible, not primary
            variant="filled"
            sx={{ mx: 0.5, my: 0.25, fontWeight: 600 }}
          />
        );
      }
      return part;
    });
  };

  const getWorkerInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const isMyMessage = (note) => {
    return currentUser && note.worker_id === currentUser.worker_id;
  };

  const handlePhotoNavigate = (direction) => {
    if (!photoPreview.allPhotos || photoPreview.allPhotos.length <= 1) return;
    
    const newIndex = direction === 'next'
      ? (photoPreview.currentIndex + 1) % photoPreview.allPhotos.length
      : (photoPreview.currentIndex - 1 + photoPreview.allPhotos.length) % photoPreview.allPhotos.length;
    
    const newPhoto = photoPreview.allPhotos[newIndex];
    setPhotoPreview({
      ...photoPreview,
      currentIndex: newIndex,
      url: newPhoto.url,
      title: newPhoto.file_name,
    });
  };

  const renderThumbnails = (photos) => {
    if (!photos || photos.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {photos.map((photo, index) => (
          <Box
            key={index}
            sx={{
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              border: '1px solid rgba(0,0,0,0.1)',
              position: 'relative',
              '&:hover': {
                opacity: 0.8,
                '& .photo-overlay': {
                  opacity: 1
                }
              }
            }}
            onClick={() => {
              const currentIndex = photos.findIndex(p => p.url === photo.url);
              setPhotoPreview({
                open: true,
                url: photo.url,
                title: photo.file_name,
                allPhotos: photos,
                currentIndex: currentIndex >= 0 ? currentIndex : 0
              });
            }}
          >
            <img
              src={photo.url}
              alt={photo.file_name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <Box
              className="photo-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                color: 'white',
                fontSize: '10px',
                textAlign: 'center',
                padding: '2px'
              }}
            >
              Click to view
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: '600px',
      display: 'flex', 
      flexDirection: 'column',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden'
    }}>
      {/* Header with Search */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider', 
        flexShrink: 0,
        backgroundColor: 'background.paper'
      }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>
          {error}
        </Alert>
      </Collapse>

      {/* Messages Container - This is the main scrollable area */}
      <Box 
        ref={messagesContainerRef}
        sx={{ 
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          minHeight: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(128,128,128,0.3)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(128,128,128,0.5)',
            }
          }
          // Removed display: flex, flexDirection: column-reverse
        }}
      >
        {/* Load More Button */}
        {hasMoreMessages && !searchQuery && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button
              onClick={loadOlderMessages}
              disabled={loadingOlder}
              startIcon={loadingOlder ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
              variant="outlined"
              size="small"
            >
              {loadingOlder ? 'Loading...' : 'Load older messages'}
            </Button>
          </Box>
        )}

        {loading && currentPage === 1 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filteredNotes.length === 0 && !searchQuery ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No notes yet.
            </Typography>
          </Box>
        ) : filteredNotes.length === 0 && searchQuery ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No messages found matching "{searchQuery}"
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 1, pb: 0 }}>
            {[...filteredNotes]
              .sort((a, b) => new Date(a.created_date) - new Date(b.created_date))
              .map((note, index, arr) => {
                const showAvatar = index === 0 || arr[index - 1].worker_id !== note.worker_id;
                const isMe = isMyMessage(note);
                
                return (
                  <ListItem 
                    key={note.note_id} 
                    sx={{ 
                      flexDirection: 'column',
                      alignItems: isMe ? 'flex-end' : 'flex-start',
                      py: 0.5,
                      px: 1
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      flexDirection: isMe ? 'row-reverse' : 'row',
                      width: '100%',
                      maxWidth: '85%',
                      alignSelf: isMe ? 'flex-end' : 'flex-start'
                    }}>
                      {/* Avatar */}
                      <Box sx={{ mx: 1, mt: 0.5 }}>
                        {showAvatar ? (
                          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                            {getWorkerInitials(note.worker_name)}
                          </Avatar>
                        ) : (
                          <Box sx={{ width: 32, height: 32 }} />
                        )}
                      </Box>

                      {/* Message Content */}
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 1.5, 
                          backgroundColor: isMe ? 'primary.main' : 'background.paper',
                          color: isMe ? 'primary.contrastText' : 'text.primary',
                          borderRadius: 2,
                          position: 'relative',
                          maxWidth: '100%',
                          wordWrap: 'break-word'
                        }}
                      >
                        {/* Name and Time */}
                        {showAvatar && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 0.5,
                            opacity: isMe ? 0.9 : 0.7
                          }}>
                            <Typography variant="caption" fontWeight="bold">
                              {isMe ? 'You' : note.worker_name}
                            </Typography>
                            <Typography variant="caption">
                              {formatNoteTime(note.created_date)}
                            </Typography>
                          </Box>
                        )}

                        {/* Message Text */}
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {renderNoteContent(note.content)}
                        </Typography>

                        {/* Photo Thumbnails */}
                        {renderThumbnails(note.photos)}
                      </Paper>
                    </Box>
                  </ListItem>
                );
              })}
          </List>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Scroll to Bottom Button */}
      {showScrollButton && (
        <Fab
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: 'absolute',
            bottom: 180,
            right: 20,
            zIndex: 1,
            opacity: 0.8
          }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}

      {/* Image Viewer Component */}
      <ImageViewer
        open={photoPreview.open}
        onClose={() => setPhotoPreview({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 })}
        imageUrl={photoPreview.url}
        fileName={photoPreview.title}
        allImages={photoPreview.allPhotos}
        currentIndex={photoPreview.currentIndex}
        onNavigate={handlePhotoNavigate}
      />

      {/* Photo Previews for New Message */}
      <Collapse in={selectedPhotos.length > 0}>
        <Paper sx={{ p: 2, m: 1, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Photos to attach:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedPhotos.map((photo, index) => (
              <Box key={index} sx={{ position: 'relative', width: 80, height: 80 }}>
                <img
                  src={photo.preview}
                  alt={photo.name}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover', 
                    borderRadius: '4px',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                />
                <IconButton
                  onClick={() => removePhoto(index)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 20,
                    height: 20
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Paper>
      </Collapse>

      {/* Message Input - Fixed at bottom */}
      <Paper sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider', 
        flexShrink: 0,
        backgroundColor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message... Use @name to mention someone"
            value={newNote}
            onChange={handleNoteChange}
            onKeyPress={handleKeyPress}
            ref={textFieldRef}
            disabled={loading}
            InputProps={{
              endAdornment: null,
              sx: { pr: 0, pb: 0.5 }
            }}
            sx={{ mr: 1 }}
          />
          <input
            type="file"
            multiple
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            id="photo-upload"
            onChange={handlePhotoSelect}
          />
          {/* Bigger, touch-friendly camera button on mobile */}
          <label htmlFor={loading ? undefined : 'photo-upload'}>
            {isMobile ? (
              <Fab
                component="span"
                color="primary"
                size="medium"
                aria-label="Add photo"
                disabled={loading}
                sx={{ mb: 0.5, mr: 0.75, boxShadow: 'none' }}
              >
                <PhotoCameraIcon />
              </Fab>
            ) : (
              <IconButton
                component="span"
                color="primary"
                disabled={loading}
                aria-label="Add photo"
                sx={{ mb: 0.5, mr: 0.5, width: 40, height: 40 }}
              >
                <PhotoCameraIcon fontSize="medium" />
              </IconButton>
            )}
          </label>
          <IconButton
            onClick={handleSubmitNote}
            disabled={(!newNote.trim() && selectedPhotos.length === 0) || loading}
            color="primary"
            sx={{ mb: 0.5 }}
          >
            {loading ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Paper>

      {/* Mention Menu */}
      <Menu
        anchorEl={mentionMenuAnchor}
        open={Boolean(mentionMenuAnchor)}
        onClose={() => setMentionMenuAnchor(null)}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: 250,
          },
        }}
      >
        {getFilteredWorkers().map((worker) => (
          <MenuItem
            key={worker.worker_id}
            onClick={() => handleMentionSelect(worker)}
          >
            <Avatar sx={{ mr: 1, width: 24, height: 24, fontSize: '0.75rem' }}>
              {getWorkerInitials(worker.name)}
            </Avatar>
            {worker.name}
          </MenuItem>
        ))}
        {getFilteredWorkers().length === 0 && (
          <MenuItem disabled>
            No team members found
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ProjectNotesTab;
